import { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff, Mail, Lock, ArrowRight, CheckCircle, AlertCircle, Loader2, Sparkles, Shield, Zap, TrendingUp, Globe, Users, Phone, PhoneCall } from 'lucide-react';
import { getGoogleLogin, getMentorLogin, GoogleLoginResponse } from './yogatrainerCredentials';
import toast, { Toaster } from 'react-hot-toast';
import { GoogleCredentialResponse, GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import { getProfileRouteForRole } from '../instrutorProfile/role-utils';
import { sendOtp, verifyOtp } from '../instrutorProfile/profileAPIs';
import { mentorPhoneLoginApi } from './yogatrainerCredentials';

const Login = () => {
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loginStep, setLoginStep] = useState('form'); // 'form', 'loading', 'success', 'error', 'role'
    const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('phone'); // 'email' or 'phone' - phone is default
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        role: 'Regular Classes',
        rememberMe: false
    });
    const [phoneNumber, setPhoneNumber] = useState('');
    const [certFile, setCertFile] = useState<File | null>(null);

    // OTP State
    const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
    const [otpSent, setOtpSent] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [isSendingOTP, setIsSendingOTP] = useState(false);
    const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
    const [otpError, setOtpError] = useState('');
    const [resendTimer, setResendTimer] = useState(0);
    const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

    const routes = useNavigate();
    const googleApiKey = process.env.REACT_APP_API_GOOGLE_OAUTH;
    const [errors, setErrors] = useState({
        email: "",
        password: "",
        phone: "",
    });
    const [bankDetails, setBankDetails] = useState({
        accountHolderName: '',
        accountNumber: '',
        ifscCode: '',
        bankName: ''
    });
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [showWelcomeAnimation, setShowWelcomeAnimation] = useState(true);

    const [googleCredential, setGoogleCredential] = useState<GoogleCredentialResponse | null>(null);

    const [additionalInfo, setAdditionalInfo] = useState({
        phone: '',
        dob: ''
    });

    const handleInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setAdditionalInfo(prev => ({ ...prev, [name]: value }));
    };
    useEffect(() => {
        const timer = setTimeout(() => setShowWelcomeAnimation(false), 2000);
        return () => clearTimeout(timer);
    }, []);

    const validateEmail = (email: string) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const calculatePasswordStrength = (password: string) => {
        let strength = 0;
        if (password.length >= 8) strength += 25;
        if (/[A-Z]/.test(password)) strength += 25;
        if (/[0-9]/.test(password)) strength += 25;
        if (/[^A-Za-z0-9]/.test(password)) strength += 25;
        return strength;
    };

    const handleInputChange = (e: any) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;

        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));

        // Real-time validation
        if (name === 'email') {
            if (value && !validateEmail(value)) {
                setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
            } else {
                setErrors(prev => ({ ...prev, email: '' }));
            }
        }

        if (name === 'password') {
            setPasswordStrength(calculatePasswordStrength(value));
            if (value.length < 6) {
                setErrors(prev => ({ ...prev, password: 'Password must be at least 6 characters' }));
            } else {
                setErrors(prev => ({ ...prev, password: '' }));
            }
        }

    };

    const validatePhone = (phone: string) => {
        return /^\d{10}$/.test(phone.replace(/\D/g, ''));
    };

    // Handle OTP input changes
    const handleOtpChange = (index: number, value: string) => {
        if (!/^\d?$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value && index < 5) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    // Handle OTP input keydown events
    const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    // Send OTP function
    const handleSendOtp = async () => {
        const phone = phoneNumber.replace(/\D/g, '');

        if (!validatePhone(phone)) {
            toast.error('Please enter a valid 10-digit phone number');
            setErrors(prev => ({ ...prev, phone: 'Please enter a valid 10-digit phone number' }));
            return;
        }

        setIsSendingOTP(true);
        setOtpError('');
        setErrors(prev => ({ ...prev, phone: '' }));

        try {
            const res = await sendOtp(phone);

            if (res.success) {
                toast.success('OTP sent successfully!');
                setOtpSent(true);
                setResendTimer(60);
                setOtp(['', '', '', '', '', '']);
                setTimeout(() => otpRefs.current[0]?.focus(), 100);
            } else {
                toast.error(res.message || 'Failed to send OTP');
            }
        } catch {
            toast.error('Failed to send OTP. Please try again.');
        } finally {
            setIsSendingOTP(false);
        }
    };

    // Verify OTP function
    const handleVerifyOtp = async () => {
        const otpValue = otp.join('');

        if (otpValue.length !== 6) {
            toast.error('Please enter complete 6-digit OTP');
            return;
        }

        setIsVerifyingOTP(true);

        try {
            const phone = phoneNumber.replace(/\D/g, '');

            // First verify OTP with your OTP service
            const otpRes = await verifyOtp(phone, otpValue);

            if (otpRes.success && otpRes.verified) {
                // toast.success('Phone number verified! Logging you in...');
                setOtpVerified(true);
                setOtpError('');

                // Auto-proceed with phone login after verification
                setTimeout(() => {
                    handlePhoneLogin(phone);
                }, 1000);
            } else {
                setOtpError(otpRes.message || 'Invalid OTP. Please try again.');
                setOtp(['', '', '', '', '', '']);
                otpRefs.current[0]?.focus();
            }
        } catch {
            setOtpError('Verification failed. Please try again.');
        } finally {
            setIsVerifyingOTP(false);
        }
    };

    // Handle phone login after OTP verification
    const handlePhoneLogin = async (phone: string) => {
        setIsLoading(true);
        setLoginStep('loading');

        try {
            const apiRes = await mentorPhoneLoginApi(phone);

            if (apiRes && apiRes.success) {
                toast.success('Login successful!');
                sessionStorage.setItem('mentorAccessToken', apiRes.token || '');
                sessionStorage.setItem('mentorId', apiRes.mentorId || '');
                window.dispatchEvent(new CustomEvent('auth-change'));
                setLoginStep('success');

                setTimeout(() => {
                    const path = getProfileRouteForRole(apiRes.role || 'trainer');
                    routes(path, { replace: true });
                }, 1500);
            } else {
                const errorMessage = apiRes?.message || 'Login failed';
                toast.error(errorMessage);
                setLoginStep('error');
                setErrors(prev => ({ ...prev, phone: errorMessage }));

                setTimeout(() => {
                    resetPhoneLogin();
                    setLoginStep('form');
                    setIsLoading(false);
                }, 1500);
            }
        } catch (error) {
            console.error("Phone login error:", error);
            setLoginStep('error');
            setErrors(prev => ({ ...prev, phone: 'Login failed. Please try again.' }));

            setTimeout(() => {
                resetPhoneLogin();
                setLoginStep('form');
                setIsLoading(false);
            }, 1500);
        }
    };

    const resetPhoneLogin = () => {
        setPhoneNumber('');
        setOtp(['', '', '', '', '', '']);
        setOtpSent(false);
        setOtpVerified(false);
        setOtpError('');
        setResendTimer(0);
        setErrors(prev => ({ ...prev, phone: '' }));
    };

    const resetEmailLogin = () => {
        setFormData(prev => ({ ...prev, email: '', password: '' }));
        setErrors(prev => ({ ...prev, email: '', password: '' }));
    };

    // Resend timer effect
    useEffect(() => {
        if (resendTimer > 0) {
            const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendTimer]);

    const handleSubmit = async (e: any) => {
        e.preventDefault();

        if (loginMethod === 'phone') {
            if (!otpVerified) {
                await handleSendOtp();
                return;
            }
            await handlePhoneLogin(phoneNumber.replace(/\D/g, ''));
            return;
        }

        // Email login
        setIsLoading(true);
        setLoginStep('loading');
        const res = await getMentorLogin(formData) as any;
        const isSuccess = Boolean(res && (res.token || res.success || res.message === "Login Successful!" || res.message === "Admin signed in successfully"));

        if (isSuccess && res.token) {
            setLoginStep('success');
            setTimeout(() => {
                sessionStorage.setItem('mentorAccessToken', res.token);
                sessionStorage.setItem('adminAccessToken', res.token);
                sessionStorage.setItem('mentorId', res.mentorId || res.admin?.mentorId || '');
                window.dispatchEvent(new CustomEvent('auth-change'));
                const role = res.role || res.admin?.role || '';
                const path = role === "Admin" ? "/admin" : getProfileRouteForRole(role);
                routes(path, { replace: true });
            }, 1000);
        } else {
            if (res && res.message && res.message.includes("pending approval")) {
                toast.error(res.message, { icon: '⏳', duration: 5001 });
            } else {
                toast.error(res?.message || 'Login failed.');
            }
            setLoginStep('error');
            setTimeout(() => {
                setLoginStep('form');
                setIsLoading(false);
            }, 2000);
        }
    };

    const handleGoogleLoginAttempt = (response: GoogleCredentialResponse) => {
        if (response?.credential) {
            setGoogleCredential(response);
            handleGoogleLogin(response);
        }
    }


    const handleGoogleLogin = async (response: GoogleCredentialResponse, role?: string, bankInfo?: any, additionalInfo?: any, file?: File | null) => {
        if (!response?.credential) return;

        try {
            setIsLoading(true);
            setLoginStep('loading');

            // Backend call
            const res: GoogleLoginResponse = await getGoogleLogin(response, role || formData.role, bankInfo, additionalInfo, file || certFile);
            if (res.success === false) {
                toast.error(res.message || 'Google login failed.');
                setLoginStep('form');
                setIsLoading(false);
                return;
            }

            // Case 1: Agar user database mein nahi mila (Backend isNewUser: true bhejega)
            if (res.isNewUser) {
                // With role-free login, we keep the default hidden role and continue.
            }

            // Case 2: User mil gaya par Admin ne Approve nahi kiya (Token nahi aaya)
            if (!res.token) {
                setLoginStep('form');
                setIsLoading(false);
                toast.success(res.message || "Details submitted! Waiting for Admin approval.", {
                    duration: 6000,
                    icon: '⏳'
                });
                return;
            }

            // Case 3: User approved hai (Token mil gaya) - Direct Dashboard
            if (res.token) {
                setLoginStep('success');
                setTimeout(() => {
                    sessionStorage.setItem('mentorAccessToken', res.token);
                    sessionStorage.setItem('mentorId', res.mentorId || '');
                    window.dispatchEvent(new CustomEvent('auth-change'));
                    const path = getProfileRouteForRole(res.role);
                    routes(path, { replace: true });
                }, 1500);
            }
        } catch (err) {
            console.error("Login Error:", err);
            toast.error('An error occurred during login.');
            setLoginStep('form');
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-purple-50 relative overflow-x-hidden">
            <Toaster />
            {/* Animated Background Elements */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-20 left-10 w-20 h-20 bg-teal-200 rounded-full opacity-30 animate-pulse"></div>
                <div className="absolute top-40 right-20 w-16 h-16 bg-purple-200 rounded-full opacity-40 animate-bounce" style={{ animationDelay: '1s' }}></div>
                <div className="absolute bottom-20 left-20 w-24 h-24 bg-teal-100 rounded-full opacity-25 animate-pulse" style={{ animationDelay: '2s' }}></div>
                <div className="absolute bottom-40 right-10 w-12 h-12 bg-purple-100 rounded-full opacity-35 animate-bounce" style={{ animationDelay: '0.5s' }}></div>
            </div>

            {/* Main Content */}
            <div className="flex items-start justify-center min-h-[calc(100vh-4rem)] px-4 py-12 sm:items-center">
                <div className="max-w-md w-full">
                    {/* Welcome Animation */}
                    {showWelcomeAnimation && (
                        <div className="fixed inset-0 bg-gradient-to-r from-teal-500 to-purple-600 flex items-center justify-center z-50">
                            <div className="text-center text-white">
                                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                                    <Sparkles className="h-10 w-10 text-teal-600" />
                                </div>
                                <h2 className="text-2xl font-bold mb-2 animate-pulse">Welcome Back!</h2>
                                <p className="text-teal-100">Continue your wellness journey...</p>
                            </div>
                        </div>
                    )}

                    {/* Vendor Call-to-Action Banner */}
                    {loginStep === 'form' && (
                        <div className={`mb-6 bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 rounded-2xl p-6 text-center shadow-2xl transform transition-all duration-700 ${!showWelcomeAnimation ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-4'}`}>
                            <div className="flex items-center justify-center mb-3">
                                <div className="flex space-x-3">
                                    <TrendingUp className="h-6 w-6 text-white animate-bounce" />
                                    <Globe className="h-6 w-6 text-white animate-pulse" />
                                    <Users className="h-6 w-6 text-white animate-bounce" style={{ animationDelay: '0.5s' }} />
                                </div>
                            </div>
                            <h3 className="text-white font-bold text-lg mb-2">
                                Grow your business with Meta Fit Wellness
                            </h3>
                            <p className="text-yellow-100 text-sm font-medium">
                                Get global visibility, more clients, and zero marketing hassle!
                            </p>
                        </div>
                    )}

                    {/* Login Card */}
                    <div className={`bg-white/90 backdrop-blur-lg rounded-2xl shadow-2xl p-8 border border-white/20 transform transition-all duration-500 ${!showWelcomeAnimation ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
                        }`}>

                        {loginStep === 'form' && (
                            <>
                                <div className="text-center mb-8">
                                    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg transform hover:scale-110 transition-transform duration-300">
                                        <span className="text-white text-2xl font-bold">M</span>
                                    </div>
                                    <h1 className="text-3xl font-bold text-gray-900 mb-2 animate-fade-in">Welcome Back, Partner!</h1>
                                    <p className="text-gray-600 animate-fade-in" style={{ animationDelay: '0.2s' }}>Where Wellness Experts Meet the World</p>
                                </div>

                                {/* Login Method Selector */}
                                <div className="flex gap-2 mb-8 bg-gray-100 p-1 rounded-lg">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setLoginMethod('phone');
                                            resetEmailLogin();
                                            setOtp(['', '', '', '', '', '']);
                                            setOtpSent(false);
                                            setOtpError('');
                                        }}
                                        className={`flex-1 py-2 px-4 rounded-md font-medium transition-all duration-200 flex items-center justify-center gap-2 ${loginMethod === 'phone'
                                                ? 'bg-white text-teal-600 shadow-md'
                                                : 'text-gray-600 hover:text-gray-900'
                                            }`}
                                    >
                                        <PhoneCall className="h-4 w-4" />
                                        <span>Phone</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setLoginMethod('email');
                                            resetPhoneLogin();
                                            resetEmailLogin();
                                        }}
                                        className={`flex-1 py-2 px-4 rounded-md font-medium transition-all duration-200 flex items-center justify-center gap-2 ${loginMethod === 'email'
                                                ? 'bg-white text-teal-600 shadow-md'
                                                : 'text-gray-600 hover:text-gray-900'
                                            }`}
                                    >
                                        <Mail className="h-4 w-4" />
                                        <span>Email</span>
                                    </button>
                                </div>

                                <div className="space-y-6">
                                    {/* Phone Login Section */}
                                    {loginMethod === 'phone' && (
                                        <>
                                            {/* Phone Number Field */}
                                            <div className="transform transition-all duration-300 hover:scale-[1.02]">
                                                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                                                    <Phone className="w-4 h-4 inline mr-2" />
                                                    Mobile Number
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <PhoneCall className={`h-5 w-5 transition-colors duration-200 ${errors.phone ? 'text-red-400' : phoneNumber ? 'text-teal-500' : 'text-gray-400'
                                                            }`} />
                                                    </div>
                                                    <input
                                                        id="phone"
                                                        name="phone"
                                                        type="tel"
                                                        autoComplete="tel"
                                                        value={phoneNumber}
                                                        onChange={(e) => {
                                                            const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                                                            setPhoneNumber(value);
                                                            if (errors.phone) {
                                                                setErrors(prev => ({ ...prev, phone: '' }));
                                                            }
                                                        }}
                                                        placeholder="Enter 10-digit mobile number"
                                                        disabled={otpSent && !otpVerified}
                                                        className={`appearance-none block w-full pl-10 pr-3 py-3 border rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200 ${errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-teal-300'
                                                            } ${(otpSent && !otpVerified) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                                    />
                                                    {phoneNumber && !errors.phone && phoneNumber.length === 10 && (
                                                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                                            <CheckCircle className="h-5 w-5 text-green-500 animate-scale-in" />
                                                        </div>
                                                    )}
                                                </div>
                                                {errors.phone && (
                                                    <p className="mt-1 text-sm text-red-600 flex items-center animate-slide-down">
                                                        <AlertCircle className="h-4 w-4 mr-1" />
                                                        {errors.phone}
                                                    </p>
                                                )}
                                            </div>

                                            {/* OTP Section */}
                                            {otpSent && (
                                                <div className="space-y-4 animate-fade-in">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-3">
                                                            Enter 6-digit OTP
                                                        </label>
                                                        <div className="flex justify-center gap-1.5 sm:gap-2">
                                                            {otp.map((digit, index) => (
                                                                <input
                                                                    key={index}
                                                                    ref={(el) => otpRefs.current[index] = el}
                                                                    type="text"
                                                                    inputMode="numeric"
                                                                    maxLength={1}
                                                                    value={digit}
                                                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                                                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                                                    className="w-9 h-10 sm:w-12 sm:h-12 text-center text-lg sm:text-xl font-bold border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none transition-colors duration-200"
                                                                    placeholder="0"
                                                                />
                                                            ))}
                                                        </div>
                                                        {otpError && (
                                                            <p className="mt-2 text-sm text-red-600 text-center animate-slide-down">
                                                                {otpError}
                                                            </p>
                                                        )}
                                                    </div>

                                                    <div className="flex gap-3">
                                                        <button
                                                            type="button"
                                                            onClick={handleVerifyOtp}
                                                            disabled={isVerifyingOTP || otp.join('').length !== 6}
                                                            className={`flex-1 py-3 rounded-lg transition-all ${isVerifyingOTP || otp.join('').length !== 6 ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} text-white font-medium`}
                                                        >
                                                            {isVerifyingOTP ? (
                                                                <span className="flex items-center justify-center gap-2">
                                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                                    Verifying...
                                                                </span>
                                                            ) : 'Verify OTP'}
                                                        </button>

                                                        <button
                                                            type="button"
                                                            onClick={resetPhoneLogin}
                                                            className="px-4 py-3 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
                                                        >
                                                            Change Number
                                                        </button>
                                                    </div>

                                                    {resendTimer > 0 ? (
                                                        <p className="text-center text-sm text-gray-500 mt-2">
                                                            Resend OTP in {resendTimer}s
                                                        </p>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={handleSendOtp}
                                                            className="text-blue-600 hover:text-blue-700 text-sm mx-auto block mt-2 transition-colors"
                                                        >
                                                            Resend OTP
                                                        </button>
                                                    )}
                                                </div>
                                            )}

                                            {/* Send OTP Button */}
                                            {!otpSent && (
                                                <button
                                                    type="button"
                                                    onClick={handleSendOtp}
                                                    disabled={isSendingOTP || !validatePhone(phoneNumber)}
                                                    className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl"
                                                >
                                                    {isSendingOTP ? (
                                                        <span className="flex items-center justify-center gap-2">
                                                            <PhoneCall className="h-4 w-4" />
                                                            Sending OTP...
                                                        </span>
                                                    ) : 'Send OTP to Login'}
                                                </button>
                                            )}

                                            {/* Verified Status */}
                                            {otpVerified && (
                                                <div className="text-center py-4">
                                                    <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full">
                                                        <CheckCircle className="h-4 w-4" />
                                                        Phone Verified
                                                    </div>
                                                    <p className="text-sm text-gray-600 mt-2">
                                                        Click the login button below to continue
                                                    </p>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {/* Email Login Section */}
                                    {loginMethod === 'email' && (
                                        <>
                                            {/* Email Field */}
                                            <div className="transform transition-all duration-300 hover:scale-[1.02]">
                                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                                    Email Address
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <Mail className={`h-5 w-5 transition-colors duration-200 ${errors.email ? 'text-red-400' : formData.email ? 'text-teal-500' : 'text-gray-400'
                                                            }`} />
                                                    </div>
                                                    <input
                                                        id="email"
                                                        name="email"
                                                        type="email"
                                                        required
                                                        value={formData.email}
                                                        onChange={handleInputChange}
                                                        className={`appearance-none block w-full pl-10 pr-3 py-3 border rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200 ${errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-teal-300'
                                                            }`}
                                                        placeholder="Enter your email"
                                                    />
                                                    {formData.email && !errors.email && (
                                                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                                            <CheckCircle className="h-5 w-5 text-green-500 animate-scale-in" />
                                                        </div>
                                                    )}
                                                </div>
                                                {errors.email && (
                                                    <p className="mt-1 text-sm text-red-600 flex items-center animate-slide-down">
                                                        <AlertCircle className="h-4 w-4 mr-1" />
                                                        {errors.email}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Password Field */}
                                            <div className="transform transition-all duration-300 hover:scale-[1.02]">
                                                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                                                    Password
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <Lock className={`h-5 w-5 transition-colors duration-200 ${errors.password ? 'text-red-400' : formData.password ? 'text-teal-500' : 'text-gray-400'
                                                            }`} />
                                                    </div>
                                                    <input
                                                        id="password"
                                                        name="password"
                                                        type={showPassword ? 'text' : 'password'}
                                                        required
                                                        value={formData.password}
                                                        onChange={handleInputChange}
                                                        className={`appearance-none block w-full pl-10 pr-10 py-3 border rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200 ${errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-teal-300'
                                                            }`}
                                                        placeholder="Enter your password"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="absolute inset-y-0 right-0 pr-3 flex items-center transform hover:scale-110 transition-transform duration-200"
                                                        onClick={() => setShowPassword(!showPassword)}>
                                                        {showPassword ? (
                                                            <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                                        ) : (
                                                            <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                                        )}
                                                    </button>
                                                </div>

                                                {errors.password && (
                                                    <p className="mt-1 text-sm text-red-600 flex items-center animate-slide-down">
                                                        <AlertCircle className="h-4 w-4 mr-1" />
                                                        {errors.password}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Remember Me & Forgot Password */}
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center transform hover:scale-105 transition-transform duration-200">
                                                    <input
                                                        id="rememberMe"
                                                        name="rememberMe"
                                                        type="checkbox"
                                                        checked={formData.rememberMe}
                                                        onChange={handleInputChange}
                                                        className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded transition-all duration-200"
                                                    />
                                                    <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-700">
                                                        Remember me
                                                    </label>
                                                </div>
                                                <a href="/forgot-password/reset-instructor-password" className="text-sm text-teal-600 hover:text-teal-500 font-medium transform hover:scale-105 transition-all duration-200">
                                                    Forgot password?
                                                </a>
                                            </div>

                                            {/* Login Button - Email */}
                                            <button
                                                type="button"
                                                onClick={handleSubmit}
                                                disabled={isLoading || !formData.email || !formData.password || !!errors.email || !!errors.password}
                                                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl">
                                                {isLoading ? (
                                                    <>
                                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                        Signing In...
                                                    </>
                                                ) : (
                                                    <>
                                                        Sign In
                                                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                                    </>
                                                )}
                                            </button>
                                        </>
                                    )}
                                </div>

                                {/* Phone Login Button - Only show when phone login method and verified */}
                                {loginMethod === 'phone' && otpVerified && (
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={isLoading}
                                        className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl">
                                        {isLoading ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Signing In...
                                            </>
                                        ) : (
                                            <>
                                                Sign In
                                                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                            </>
                                        )}
                                    </button>
                                )}
                            </>
                        )}

                        {/* Loading State */}
                        {loginStep === 'loading' && (
                            <div className="text-center py-12 animate-fade-in">
                                <div className="w-16 h-16 bg-gradient-to-r from-teal-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                                    <Loader2 className="h-8 w-8 text-white animate-spin" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">Signing you in...</h3>
                                <p className="text-gray-600">Please wait while we verify your credentials</p>
                            </div>
                        )}

                        {/* Success State */}
                        {loginStep === 'success' && (
                            <div className="text-center py-12 animate-fade-in">
                                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                                    <CheckCircle className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">Welcome back!</h3>
                                <p className="text-gray-600">Redirecting to your dashboard...</p>
                            </div>
                        )}

                        {/* Error State */}
                        {loginStep === 'error' && (
                            <div className="text-center py-12 animate-fade-in">
                                <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                                    <AlertCircle className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">Login Failed</h3>
                                <p className="text-gray-600 mb-4">Invalid credentials. Please try again.</p>
                            </div>
                        )}

                        {/* Social Login - Only show in form state */}
                        {loginStep === 'form' && (
                            <div className="mt-6 animate-fade-in" style={{ animationDelay: '0.4s' }}>
                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-gray-300" />
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className="px-2 bg-white text-gray-500">Or continue with</span>
                                    </div>
                                </div>

                                <div className="w-full mt-6 flex justify-center">
                                    <GoogleOAuthProvider clientId={googleApiKey || ''}>
                                        <GoogleLogin
                                            onSuccess={handleGoogleLoginAttempt}
                                            onError={() => {
                                                console.error("[INFO]: Google login failed.");
                                                toast.error("Google login failed. Please try again.");
                                            }}
                                            useOneTap={false}
                                            type="standard"
                                            theme="outline"
                                            size="large"
                                            text="signin_with"
                                            shape="rectangular"
                                            logo_alignment="left" width={300}
                                        />
                                    </GoogleOAuthProvider>
                                </div>
                            </div>
                        )}

                        {/* Sign Up Link - Only show in form state */}
                        {loginStep === 'form' && (
                            <div className="text-center mt-6 animate-fade-in" style={{ animationDelay: '0.6s' }}>
                                <p className="text-sm text-gray-600">
                                    Don't have an account?{' '}
                                    <a href="/trainer-signup" className="font-medium text-teal-600 hover:text-teal-600 transform hover:scale-105 transition-all duration-200 inline-block">
                                        Sign up for free
                                    </a>
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Security Features */}
                    {loginStep === 'form' && (
                        <div className="mt-6 bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-white/20 animate-fade-in" style={{ animationDelay: '0.8s' }}>
                            <div className="flex items-center justify-center space-x-6">
                                <div className="flex items-center text-sm text-gray-600">
                                    <Shield className="h-4 w-4 text-teal-600 mr-2" />
                                    SSL Encrypted
                                </div>
                                <div className="flex items-center text-sm text-gray-600">
                                    <Zap className="h-4 w-4 text-teal-600 mr-2" />
                                    Fast & Secure
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Additional Info */}
                    <div className="text-center mt-6 animate-fade-in" style={{ animationDelay: '1s' }}>
                        <p className="text-xs text-gray-500">
                            By signing in, you agree to our{' '}
                            <a href="#" className="text-teal-600 hover:teal-teal-500 transition-colors duration-200">Terms of Service</a>{' '}
                            and{' '}
                            <a href="#" className="text-teal-600 hover:text-teal-500 transition-colors duration-200">Privacy Policy</a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
